import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../../stores/authStore';
import { adminUsersService, type AdminUser, type AdminUsersListResponse, type ListUsersFilters } from '../../../services';

const UsersManagementPage: React.FC = () => {
  const { user } = useAuthStore();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [list, setList] = useState<AdminUsersListResponse | null>(null);
  const [filters, setFilters] = useState<ListUsersFilters>({ page: 1, page_size: 20 });
  const [deletingId, setDeletingId] = useState<string | null>(null);

  useEffect(() => {
    if (!user || (user.role !== 'admin' && user.role !== 'super_admin')) {
      navigate('/dashboard', { replace: true });
      return;
    }
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, filters]);

  const load = async () => {
    setLoading(true);
    try {
      const res = await adminUsersService.listUsers(filters);
      setList(res);
    } finally {
      setLoading(false);
    }
  };

  const roles = [
    { value: 'customer', label: 'مشتری', color: 'bg-blue-50 text-blue-700 border-blue-100' },
    { value: 'wholesale', label: 'عمده‌فروش', color: 'bg-purple-50 text-purple-700 border-purple-100' },
    { value: 'retailer', label: 'خرده‌فروش', color: 'bg-indigo-50 text-indigo-700 border-indigo-100' },
    { value: 'goldsmith', label: 'طلاساز', color: 'bg-amber-50 text-amber-700 border-amber-100' },
    { value: 'admin', label: 'ادمین', color: 'bg-red-50 text-red-700 border-red-100' },
    { value: 'super_admin', label: 'مدیر سیستم', color: 'bg-rose-50 text-rose-700 border-rose-100' },
  ];

  const getRoleBadge = (role: string) => {
    const r = roles.find(x => x.value === role);
    return (
      <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium border ${r?.color || 'bg-gray-50 text-gray-600 border-gray-200'}`}>
        {r?.label || role}
      </span>
    );
  };

  const getStatusBadge = (status: string) => {
    const isActive = status === 'active';
    return (
      <span className={`flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium border ${isActive ? 'bg-green-50 text-green-700 border-green-200' : 'bg-gray-50 text-gray-600 border-gray-200'}`}>
        <span className={`w-1.5 h-1.5 rounded-full ${isActive ? 'bg-green-500' : 'bg-gray-400'}`}></span>
        {isActive ? 'فعال' : 'غیرفعال'}
      </span>
    );
  };

  return (
    <div className="p-4 lg:p-8 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">مدیریت کاربران</h1>
          <p className="text-gray-500 mt-1">لیست کاربران، مدیریت دسترسی‌ها و نقش‌ها</p>
        </div>
        <button 
          className="px-6 py-2.5 rounded-xl bg-[#0e444e] text-white hover:bg-[#0b363e] shadow-lg shadow-[#0e444e]/20 font-medium transition-all flex items-center gap-2 justify-center sm:justify-start"
          onClick={() => navigate('/admin/users/new')}
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          ایجاد کاربر جدید
        </button>
      </div>

      {/* Filters */}
      <div className="card mb-6 p-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="relative">
            <input 
              className="input-field pl-10" 
              placeholder="جستجو (نام یا شماره)" 
              value={filters.search || ''} 
              onChange={(e) => setFilters({ ...filters, search: e.target.value, page: 1 })} 
            />
            <svg className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </div>
          <select 
            className="input-field" 
            value={filters.role || ''} 
            onChange={(e) => setFilters({ ...filters, role: e.target.value as AdminUser['role'] | '', page: 1 })}
          >
            <option value="">همه نقش‌ها</option>
            {roles.map((r) => (<option key={r.value} value={r.value}>{r.label}</option>))}
          </select>
          <select 
            className="input-field" 
            value={filters.status || ''} 
            onChange={(e) => setFilters({ ...filters, status: e.target.value, page: 1 })}
          >
            <option value="">همه وضعیت‌ها</option>
            <option value="active">فعال</option>
            <option value="inactive">غیرفعال</option>
          </select>
          <button 
            className="px-4 py-2.5 rounded-xl bg-gray-100 text-gray-700 hover:bg-gray-200 font-medium transition-colors" 
            onClick={load}
          >
            بروزرسانی لیست
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="card overflow-hidden p-0">
        {loading ? (
          <div className="p-12 text-center">
            <div className="w-8 h-8 border-4 border-[--color-accent] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-gray-500">در حال بارگزاری اطلاعات...</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm text-right">
              <thead className="bg-gray-50 border-b border-gray-100">
                <tr>
                  <th className="px-6 py-4 font-semibold text-gray-700">کاربر</th>
                  <th className="px-6 py-4 font-semibold text-gray-700">شماره موبایل</th>
                  <th className="px-6 py-4 font-semibold text-gray-700">نقش</th>
                  <th className="px-6 py-4 font-semibold text-gray-700">وضعیت</th>
                  <th className="px-6 py-4 font-semibold text-gray-700 text-left">عملیات</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {list?.users.map((u) => (
                  <tr key={u.id} className="hover:bg-gray-50/50 transition-colors group">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-[--color-accent]/10 text-[--color-accent] flex items-center justify-center font-bold text-lg">
                          {u.full_name?.charAt(0) || '?'}
                        </div>
                        <div>
                          <div className="font-medium text-gray-900">{u.full_name || 'بدون نام'}</div>
                          <div className="text-xs text-gray-500 font-mono mt-0.5">{u.id.substring(0, 8)}...</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 font-mono text-gray-600" dir="ltr">{u.phone_number}</td>
                    <td className="px-6 py-4">{getRoleBadge(u.role)}</td>
                    <td className="px-6 py-4">{getStatusBadge(u.status)}</td>
                    <td className="px-6 py-4">
                      <div className="flex items-center justify-end gap-2 opacity-100 lg:opacity-0 lg:group-hover:opacity-100 transition-opacity">
                        <button
                          className="p-2 rounded-lg text-blue-600 hover:bg-blue-50 transition-colors"
                          title="ویرایش"
                          onClick={() => navigate(`/admin/users/${u.id}/edit`)}
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                        </button>
                        <button
                          className="p-2 rounded-lg text-red-600 hover:bg-red-50 transition-colors"
                          title="حذف"
                          disabled={deletingId === u.id}
                          onClick={async () => {
                            if (window.confirm('آیا از حذف این کاربر اطمینان دارید؟')) {
                              setDeletingId(u.id);
                              try {
                                await adminUsersService.deleteUser(u.id);
                                await load();
                              } finally {
                                setDeletingId(null);
                              }
                            }
                          }}
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                {list?.users.length === 0 && (
                  <tr>
                    <td colSpan={5} className="px-6 py-12 text-center text-gray-500">
                      کاربری یافت نشد
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Pagination */}
      {(list?.total_pages ?? 1) > 1 && (
        <div className="flex justify-center items-center gap-2 mt-8">
          <button
            onClick={() => setFilters({ ...filters, page: Math.max(1, (filters.page || 1) - 1) })}
            disabled={(filters.page || 1) <= 1}
            className="p-2 rounded-lg bg-white border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-50 transition-colors"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
          <span className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-lg">
            صفحه {filters.page || 1} از {list?.total_pages ?? 1}
          </span>
          <button
            onClick={() => setFilters({ ...filters, page: Math.min(list?.total_pages ?? 1, (filters.page || 1) + 1) })}
            disabled={(filters.page || 1) >= (list?.total_pages ?? 1)}
            className="p-2 rounded-lg bg-white border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-50 transition-colors"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
        </div>
      )}
    </div>
  );
};

export default UsersManagementPage;